import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';

import { Card } from 'core/components/card';
import CollapsibleCard from 'core/components/collapsible-card';
import { Label } from 'core/components/label';
import { Loading } from 'core/components/loading';
import { useToast } from 'core/components/toast';
import { Task } from './components/task';
import { TaskDocumentModal } from './components/task-document-modal';
import darkGreenCheck from 'core/assets/svg/dark-green-check.svg';

import useStyles from './onboarding-tasks.styles';

/**
 * Offer onboarding tasks component
 * Displays tasks for an offer (pre-purchase)
 *
 * @param {Object} props
 * @param {string} props.offerId - The offer ID
 * @param {Array} props.tasks - Array of task objects
 * @param {Array} props.incompleteTasks - Array of incomplete task objects
 * @param {Array} props.completedTasks - Array of completed task objects
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message if any
 * @param {Function} props.onUploadDocument - Callback when document is uploaded
 * @param {Function} props.onDownloadDocument - Callback when document is downloaded
 * @param {boolean} props.canUploadDocuments - Whether user can upload documents
 * @param {boolean} props.isAgency - Whether user is an agency
 * @param {Array} props.allowedTasksForUpload - Array of task names that can be uploaded
 */
const OnboardingTasks = ({
  offerId,
  tasks = [],
  incompleteTasks = [],
  completedTasks = [],
  loading = false,
  error = null,
  onUploadDocument,
  onDownloadDocument,
  onDeleteDocument,
  canUploadDocuments = false,
  isAgency = false,
  allowedTasksForUpload = []
}) => {
  const classes = useStyles();
  const toast = useToast();

  // Global modal state that persists across task re-renders
  const [modalState, setModalState] = useState({
    open: false,
    taskName: null,
    taskId: null,
    files: [],
    uploading: false
  });

  const handleOpenModal = (taskName, taskId, files) => {
    setModalState({
      open: true,
      taskName,
      taskId,
      files,
      uploading: false
    });
  };

  const handleCloseModal = () => {
    if (!modalState.uploading) {
      setModalState((prev) => ({ ...prev, open: false }));
    }
  };

  const handleModalUpload = async (files, onProgress) => {
    setModalState((prev) => ({ ...prev, uploading: true }));
    try {
      // Handle both single file and multiple files
      const fileArray = Array.isArray(files) ? files : [files];

      // Upload each file individually
      for (const file of fileArray) {
        await onUploadDocument({
          file,
          taskId: modalState.taskId,
          taskName: modalState.taskName,
          onProgress
        });
      }
      // Don't close modal - let user upload more files or close manually
    } finally {
      setModalState((prev) => ({ ...prev, uploading: false }));
    }
  };

  // Update modal files when tasks change
  useEffect(() => {
    if (modalState.open && modalState.taskId) {
      const currentTask = tasks.find((task) => task.taskId === modalState.taskId);
      if (currentTask) {
        setModalState((prev) => ({ ...prev, files: currentTask.files || [] }));
      }
    }
  }, [tasks, modalState.open, modalState.taskId]);

  useEffect(() => {
    if (error) {
      toast.notify({
        type: 'error',
        message: error
      });
    }
  }, [error, toast]);

  return (
    <div className={classes.sectionContent}>
      {loading ? (
        <Loading />
      ) : (
        <>
          {tasks.length > 0 && (
            <Card style={{ overflow: 'visible' }}>
              {incompleteTasks.length ? (
                incompleteTasks.map((task) => {
                  const isTaskUploadEnabled =
                    canUploadDocuments && (isAgency ? allowedTasksForUpload.includes(task.taskName) : true);

                  return (
                    <Task
                      key={task.taskId || `${offerId}-${task.taskName}`}
                      {...task}
                      entityId={offerId}
                      entityType="offer"
                      readOnly={!isTaskUploadEnabled}
                      onUpload={onUploadDocument}
                      onDownload={onDownloadDocument}
                      onDelete={onDeleteDocument}
                      onOpenModal={handleOpenModal}
                      canReset={false}
                      isAgency={isAgency}
                    />
                  );
                })
              ) : (
                <div className={classes.container}>
                  <img src={darkGreenCheck} alt="Rounded checkmark" />
                  <Label type="darkGreenSmall">All tasks that can be completed early are ready for review.</Label>
                </div>
              )}
              <CollapsibleCard
                showChevron={Boolean(completedTasks.length)}
                className={classes.collapsible}
                initialOpen={true}
                content={
                  <Grid container alignItems="center" justifyContent="center">
                    <Label type="infoSubtitle" className={classes.greenLabel}>
                      Ready for review
                    </Label>
                    <Label type="italicLightSmall">{completedTasks.length} task(s)</Label>
                  </Grid>
                }
                type="noBorders"
              >
                {!completedTasks.length && (
                  <Grid container className={classes.noTasksMessage}>
                    <Label type="italicDark">
                      Nothing here yet—upload documents to prepare tasks for review after purchase.
                    </Label>
                  </Grid>
                )}
                {completedTasks.map((task) => (
                  <Task
                    key={task.taskId || `${offerId}-${task.taskName}`}
                    {...task}
                    entityId={offerId}
                    entityType="offer"
                    onUpload={onUploadDocument}
                    onDownload={onDownloadDocument}
                    onDelete={onDeleteDocument}
                    onOpenModal={handleOpenModal}
                    canReset={false}
                    isAgency={isAgency}
                  />
                ))}
              </CollapsibleCard>
            </Card>
          )}
        </>
      )}

      {/* Global modal that persists across task re-renders */}
      <TaskDocumentModal
        open={modalState.open}
        onClose={handleCloseModal}
        taskName={modalState.taskName}
        onUpload={handleModalUpload}
        uploading={modalState.uploading}
        existingFiles={modalState.files}
        onDownload={onDownloadDocument}
        onDelete={onDeleteDocument}
      />
    </div>
  );
};

OnboardingTasks.propTypes = {
  offerId: PropTypes.string.isRequired,
  tasks: PropTypes.array,
  incompleteTasks: PropTypes.array,
  completedTasks: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onUploadDocument: PropTypes.func,
  onDownloadDocument: PropTypes.func,
  onDeleteDocument: PropTypes.func,
  canUploadDocuments: PropTypes.bool,
  isAgency: PropTypes.bool,
  allowedTasksForUpload: PropTypes.array
};

export default observer(OnboardingTasks);
